import React from "react";
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { CameraController } from "./CameraController";
import { FixedGrid } from "./FixedGrid";
import { GridClickHandler } from "./GridClickHandler";
import { DragPlane } from "./DragPlane";
import { CameraAwareSelection } from "./CameraAwareSelection";
import { DragSelectionHandler } from "./DragSelectionHandler";
import { NodeDragHandler } from "./NodeDragHandler";
import { EnhancedOrbitControls } from "./EnhancedOrbitControls";
import { NodeIDLabels } from "./NodeIDLabels";
import { BeamIDLabels } from "./BeamIDLabels";
import { SelectedNodeIDLabels } from "./SelectedNodeIDLabels";
import { SelectedBeamIDLabels } from "./SelectedBeamIDLabels";
import type { ViewType } from "../../utils/workspace.types";
import {
  CAMERA_CONFIG,
  LIGHTING_CONFIG,
} from "../../utils/workspace.constants";

interface Canvas3DProps {
  currentView: ViewType;
  isGridVisible: boolean;
  isNodeCreationMode: boolean;
  isBeamCreationMode: boolean;
  zoomToCursor: boolean;
  isDragging: boolean;
  isSelectionMode: boolean;
  isCtrlPressed: boolean;
  children?: React.ReactNode;
  onGridClick: (point: THREE.Vector3) => void;
  // onCanvasDoubleClick removed - selection controlled via ViewMenu only
  onSelectionMouseDown: (event: React.MouseEvent) => void;
  onSelectionMouseMove: (event: React.MouseEvent) => void;
  onSelectionMouseUp: (event: React.MouseEvent) => void;
}

/**
 * Canvas3D component - Three.js canvas container
 * Extracted from STAADWorkspace3D to maintain exact behavior
 */
export const Canvas3D: React.FC<Canvas3DProps> = ({
  currentView,
  isGridVisible,
  isNodeCreationMode,
  isBeamCreationMode,
  zoomToCursor,
  isDragging,
  isSelectionMode,
  isCtrlPressed,
  children,
  onGridClick,
  onSelectionMouseDown,
  onSelectionMouseMove,
  onSelectionMouseUp,
}) => {
  return (
    <Canvas
      camera={{
        position: CAMERA_CONFIG.DEFAULT_POSITION,
        fov: CAMERA_CONFIG.FOV,
        near: CAMERA_CONFIG.NEAR,
        far: CAMERA_CONFIG.FAR,
        up: new THREE.Vector3(...CAMERA_CONFIG.UP),
      }}
      gl={{ 
        preserveDrawingBuffer: true,
        antialias: true,
      }}
      style={{
        background: "#f0f0f0",
        cursor: isSelectionMode ? "crosshair" : "default",
      }}
      // Double-click handler removed - selection controlled via ViewMenu buttons only
    >
      {/* Grid click handler for node creation */}
      {isNodeCreationMode && (
        <GridClickHandler
          onGridClick={onGridClick}
          currentView={currentView}
          isBeamCreationMode={isBeamCreationMode}
        />
      )}

      {/* Camera controller */}
      <CameraController
        currentView={currentView}
        projection={
          currentView === "isometric" ? "perspective" : "orthographic"
        }
      />

      {/* Camera-aware selection component */}
      <CameraAwareSelection currentView={currentView} />

      {/* Drag selection handler for empty space selection */}
      <DragSelectionHandler
        isSelectionMode={isSelectionMode}
        isCtrlPressed={isCtrlPressed}
        onSelectionMouseDown={onSelectionMouseDown}
        onSelectionMouseMove={onSelectionMouseMove}
        onSelectionMouseUp={onSelectionMouseUp}
      />

      {/* Enhanced lighting for better visibility */}
      <ambientLight intensity={LIGHTING_CONFIG.AMBIENT_INTENSITY} />
      <directionalLight
        position={LIGHTING_CONFIG.DIRECTIONAL_POSITION_1}
        intensity={LIGHTING_CONFIG.DIRECTIONAL_INTENSITY}
        castShadow
      />
      <directionalLight
        position={LIGHTING_CONFIG.DIRECTIONAL_POSITION_2}
        intensity={LIGHTING_CONFIG.DIRECTIONAL_SECONDARY_INTENSITY}
      />

      {/* Fixed grid that won't rotate with camera */}
      {isGridVisible && <FixedGrid currentView={currentView} />}

      {/* Invisible drag plane for better interaction */}
      {isDragging && <DragPlane />}

      {/* Enhanced Orbit Controls - Engineering Software Style */}
      <EnhancedOrbitControls
        currentView={currentView}
        isNodeCreationMode={isNodeCreationMode}
        zoomToCursor={zoomToCursor}
      />

      {/* Node drag handler: enables Ctrl+Left Click planar dragging and tooltip in planar views */}
      <NodeDragHandler currentView={currentView} />

      {/* ID Labels - controlled by UIStore toggles */}
      <NodeIDLabels />
      <BeamIDLabels />

      {/* Selected Element ID Labels - always shown for selected elements */}
      <SelectedNodeIDLabels />
      <SelectedBeamIDLabels />

      {/* Child components (nodes, beams, etc.) */}
      {children}
    </Canvas>
  );
};
